import React, { useState, useEffect, useMemo } from "react";
import { useBuilding } from "../../context/BuildingContext";
import { useIsMobile } from "../../hooks/useResponsive";
import { db } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { tolerantId } from "../../utils/firestoreHelpers";
import {
  LuCalendarDays,
  LuChevronLeft,
  LuChevronRight,
  LuLoader,
  LuTriangleAlert,
  LuClock,
  LuInfo,
  LuBriefcase,
} from "react-icons/lu";

const DAYS_OF_WEEK = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const SHIFT_TYPE_COLORS = {
  regular: "#0080ff",
  extra: "#f59e0b",
  feriado: "#ef4444",
};

const TYPE_ABBREVIATIONS = {
  reglamentaria: "Reglam.",
  extraordinaria: "Extrao.",
  estudio: "Estudio",
  maternidad_paternidad: "Mat/Pat",
  sin_goce: "S/Goce",
  enfermedad: "Enferm.",
  licencia: "Licencia",
  otro: "Otro",
};

const TYPE_LABELS = {
  reglamentaria: "Reglamentaria",
  extraordinaria: "Extraordinaria",
  estudio: "Por Estudio",
  maternidad_paternidad: "Maternidad/Paternidad",
  sin_goce: "Sin Goce de Sueldo",
  enfermedad: "Por Enfermedad",
  licencia: "Licencia",
  otro: "Otro",
};

// Expand date range to array of YYYY-MM-DD strings
const getDateRange = (from, to) => {
  if (!from) return [];
  const dates = [];
  const current = new Date(from + "T12:00:00");
  const end = to ? new Date(to + "T12:00:00") : current;
  while (current <= end) {
    dates.push(current.toISOString().split("T")[0]);
    current.setDate(current.getDate() + 1);
  }
  return dates;
};

const TurnosEmployee = ({ userid }) => {
  const isMobile = useIsMobile();
  const { selectedBuilding } = useBuilding();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [shifts, setShifts] = useState([]);
  const [leaves, setLeaves] = useState([]); // approved licencias
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [scheduleInfo, setScheduleInfo] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  useEffect(() => {
    if (selectedBuilding) {
      loadData();
      loadScheduleInfo();
    } else {
      setLoading(false);
    }
  }, [selectedBuilding, year, month]);

  const loadData = async () => {
    setLoading(true);
    try {
      const buildingIdVariants = tolerantId(selectedBuilding.buildingid);
      const empId = String(userid);

      // Load shifts for this month
      const startDate = `${year}-${String(month + 1).padStart(2, "0")}-01`;
      const endMonth = month === 11 ? 1 : month + 2;
      const endYear = month === 11 ? year + 1 : year;
      const endDate = `${endYear}-${String(endMonth).padStart(2, "0")}-01`;

      const shiftsQ = query(
        collection(db, "employee_shifts"),
        where("buildingid", "in", buildingIdVariants),
        where("employeeid", "==", empId),
        where("date", ">=", startDate),
        where("date", "<", endDate)
      );

      // Load approved licencias for this employee
      const leavesQ = query(
        collection(db, "employee_requests"),
        where("buildingid", "in", buildingIdVariants),
        where("employeeid", "==", empId),
        where("status", "==", "aprobada")
      );

      const [shiftsSnap, leavesSnap] = await Promise.all([
        getDocs(shiftsQ),
        getDocs(leavesQ),
      ]);

      const shiftsData = [];
      shiftsSnap.forEach((d) => shiftsData.push({ id: d.id, ...d.data() }));
      setShifts(shiftsData);

      const leavesData = [];
      leavesSnap.forEach((d) => leavesData.push({ id: d.id, ...d.data() }));
      setLeaves(leavesData);
    } catch (error) {
      console.error("Error loading shifts/leaves:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadScheduleInfo = async () => {
    try {
      const qEmpBuilding = query(
        collection(db, "employeebuilding"),
        where("building", "in", tolerantId(selectedBuilding.buildingid)),
        where("employeeid", "in", tolerantId(userid))
      );
      const empBuildingSnap = await getDocs(qEmpBuilding);
      const empDocs = empBuildingSnap.docs;

      if (empDocs.length > 0) {
        const data = empDocs[0].data();
        setScheduleInfo({
          horario: data.horario || null,
          diasTrabajo: data.diasTrabajo || null,
        });
      }
    } catch (error) {
      console.error("Error loading schedule info:", error);
    }
  };

  // Build a map of dateStr → leave for the visible month
  const leavesByDate = useMemo(() => {
    const map = {};
    leaves.forEach((leave) => {
      if (!leave.dateFrom) return;
      const dates = getDateRange(leave.dateFrom, leave.dateTo);
      dates.forEach((dateStr) => {
        // Only include dates in the current month view
        const [y, m] = dateStr.split("-").map(Number);
        if (y === year && m === month + 1) {
          map[dateStr] = leave;
        }
      });
    });
    return map;
  }, [leaves, year, month]);

  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
    setSelectedDay(null);
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
    setSelectedDay(null);
  };

  const getDaysInMonth = () => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = () => new Date(year, month, 1).getDay();

  const getShiftForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return shifts.find((s) => s.date === dateStr);
  };

  const getLeaveForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return leavesByDate[dateStr] || null;
  };

  const isToday = (day) => {
    const now = new Date();
    return day === now.getDate() && month === now.getMonth() && year === now.getFullYear();
  };

  const daysInMonth = getDaysInMonth();
  const firstDay = getFirstDayOfMonth();
  const calendarDays = [];

  for (let i = 0; i < firstDay; i++) calendarDays.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarDays.push(d);

  const selectedShift = selectedDay ? getShiftForDay(selectedDay) : null;
  const selectedLeave = selectedDay ? getLeaveForDay(selectedDay) : null;

  if (!selectedBuilding) {
    return (
      <div style={styles.emptyContainer}>
        <LuTriangleAlert size={48} color="#f59e0b" />
        <h2 style={styles.emptyTitle}>Sin edificio asignado</h2>
      </div>
    );
  }

  return (
    <div style={{ ...styles.container, padding: isMobile ? 16 : 24 }}>
      {/* Header */}
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Mi Horario</h1>
        <p style={styles.pageSubtitle}>{selectedBuilding.name}</p>
      </div>

      {/* Schedule info */}
      {scheduleInfo && (scheduleInfo.horario || scheduleInfo.diasTrabajo) && (
        <div style={styles.infoCard}>
          <LuInfo size={16} color="#0080ff" />
          <div style={styles.infoContent}>
            {scheduleInfo.horario && (
              <span style={styles.infoText}>Horario: <strong>{scheduleInfo.horario}</strong></span>
            )}
            {scheduleInfo.diasTrabajo && (
              <span style={styles.infoText}>Días de trabajo: <strong>{scheduleInfo.diasTrabajo}</strong></span>
            )}
          </div>
        </div>
      )}

      {/* Calendar */}
      <div style={styles.calendarCard}>
        <div style={styles.calendarHeader}>
          <button style={styles.navBtn} onClick={prevMonth}>
            <LuChevronLeft size={14} />
          </button>
          <h2 style={styles.monthTitle}>
            {MONTHS[month]} {year}
          </h2>
          <button style={styles.navBtn} onClick={nextMonth}>
            <LuChevronRight size={14} />
          </button>
        </div>

        {loading ? (
          <div style={styles.loadingCenter}>
            <LuLoader size={24} color="#0080ff" style={{ animation: "spin 1s linear infinite" }} />
          </div>
        ) : (
          <>
            <div style={styles.calendarGrid}>
              {DAYS_OF_WEEK.map((day) => (
                <div key={day} style={styles.dayHeader}>
                  {day}
                </div>
              ))}

              {calendarDays.map((day, idx) => {
                if (day === null) {
                  return <div key={`empty-${idx}`} style={styles.emptyCell} />;
                }

                const shift = getShiftForDay(day);
                const leave = getLeaveForDay(day);
                const today = isToday(day);
                const isSelected = selectedDay === day;

                // Determine cell color
                let cellBg = "#f3f4f6"; // default: grey (no work)
                let cellBorder = "1px solid #e5e7eb";

                if (leave) {
                  cellBg = "#dbeafe"; // blue for OoO
                  cellBorder = "1px solid #93c5fd";
                } else if (shift) {
                  cellBg = "#dcfce7"; // green for work day
                  cellBorder = "1px solid #86efac";
                }

                if (isSelected) {
                  cellBorder = "2px solid #0080ff";
                }

                return (
                  <div
                    key={day}
                    style={{
                      ...styles.dayCell,
                      backgroundColor: cellBg,
                      border: cellBorder,
                      cursor: (shift || leave) ? "pointer" : "default",
                    }}
                    onClick={() => (shift || leave) && setSelectedDay(day)}
                  >
                    <span style={{
                      ...styles.dayNumber,
                      ...(today ? styles.todayNumber : {}),
                    }}>
                      {day}
                    </span>
                    {leave ? (
                      <div style={styles.oooIndicator}>
                        <span style={styles.oooText}>OoO</span>
                        <span style={styles.oooType}>
                          {TYPE_ABBREVIATIONS[leave.type] || leave.type}
                        </span>
                      </div>
                    ) : shift ? (
                      <div style={styles.shiftIndicator}>
                        <div style={{
                          ...styles.shiftDot,
                          backgroundColor: SHIFT_TYPE_COLORS[shift.type] || "#0080ff",
                        }} />
                        <span style={styles.shiftTimeSmall}>
                          {shift.startTime}
                        </span>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div style={styles.legend}>
              <div style={styles.legendItem}>
                <div style={{ ...styles.legendDot, backgroundColor: "#16a34a" }} />
                <span style={styles.legendText}>Día de trabajo</span>
              </div>
              <div style={styles.legendItem}>
                <div style={{ ...styles.legendDot, backgroundColor: "#d1d5db" }} />
                <span style={styles.legendText}>No trabaja</span>
              </div>
              <div style={styles.legendItem}>
                <div style={{ ...styles.legendDot, backgroundColor: "#3b82f6" }} />
                <span style={styles.legendText}>Licencia (OoO)</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Selected day detail */}
      {selectedLeave && (
        <div style={styles.detailCard}>
          <div style={styles.detailHeader}>
            <LuBriefcase size={16} color="#3b82f6" />
            <h3 style={styles.detailTitle}>
              OoO — {TYPE_LABELS[selectedLeave.type] || selectedLeave.type}
            </h3>
          </div>
          <div style={styles.detailBody}>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Título</span>
              <span style={styles.detailValue}>{selectedLeave.title}</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Período</span>
              <span style={styles.detailValue}>
                {selectedLeave.dateFrom}{selectedLeave.dateTo ? ` — ${selectedLeave.dateTo}` : ""}
              </span>
            </div>
            {selectedLeave.description && (
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Detalle</span>
                <span style={styles.detailValue}>{selectedLeave.description}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {selectedShift && !selectedLeave && (
        <div style={styles.detailCard}>
          <div style={styles.detailHeader}>
            <LuClock size={16} color="#0080ff" />
            <h3 style={styles.detailTitle}>
              Horario del {selectedDay} de {MONTHS[month]}
            </h3>
          </div>
          <div style={styles.detailBody}>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Horario</span>
              <span style={styles.detailValue}>
                {selectedShift.startTime} — {selectedShift.endTime}
              </span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Tipo</span>
              <span style={{
                ...styles.typeBadge,
                backgroundColor: (SHIFT_TYPE_COLORS[selectedShift.type] || "#0080ff") + "20",
                color: SHIFT_TYPE_COLORS[selectedShift.type] || "#0080ff",
              }}>
                {selectedShift.type === "regular" ? "Regular" : selectedShift.type === "extra" ? "Extra" : "Feriado"}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: 24,
    maxWidth: 700,
    margin: "0 auto",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  emptyContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "60vh",
    gap: 12,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: 400,
    color: "#111827",
  },
  pageHeader: {
    marginBottom: 20,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 400,
    color: "#111827",
    margin: 0,
  },
  pageSubtitle: {
    fontSize: 14,
    color: "#6b7280",
    margin: "4px 0 0",
  },
  infoCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: 16,
    backgroundColor: "#f0f7ff",
    borderRadius: 12,
    border: "1px solid #bfdbfe",
    marginBottom: 20,
  },
  infoContent: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  infoText: {
    fontSize: 13,
    color: "#374151",
  },
  calendarCard: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    padding: 20,
    marginBottom: 20,
  },
  calendarHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  navBtn: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: "#f3f4f6",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#374151",
  },
  monthTitle: {
    fontSize: 18,
    fontWeight: 400,
    color: "#111827",
    margin: 0,
  },
  loadingCenter: {
    display: "flex",
    justifyContent: "center",
    padding: 48,
  },
  calendarGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: 4,
  },
  dayHeader: {
    textAlign: "center",
    fontSize: 12,
    fontWeight: 400,
    color: "#6b7280",
    padding: "8px 0",
  },
  emptyCell: {
    aspectRatio: "1",
  },
  dayCell: {
    aspectRatio: "1",
    borderRadius: 8,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    transition: "all 0.15s ease",
    position: "relative",
    padding: 2,
  },
  dayNumber: {
    fontSize: 14,
    fontWeight: 400,
    color: "#374151",
  },
  todayNumber: {
    backgroundColor: "#0080ff",
    color: "#ffffff",
    width: 28,
    height: 28,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 400,
  },
  oooIndicator: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 0,
  },
  oooText: {
    fontSize: 9,
    fontWeight: 400,
    color: "#1d4ed8",
    lineHeight: 1,
  },
  oooType: {
    fontSize: 7,
    fontWeight: 400,
    color: "#3b82f6",
    lineHeight: 1,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textAlign: "center",
  },
  shiftIndicator: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 1,
  },
  shiftDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
  },
  shiftTimeSmall: {
    fontSize: 9,
    color: "#6b7280",
    fontWeight: 400,
  },
  legend: {
    display: "flex",
    justifyContent: "center",
    gap: 20,
    marginTop: 16,
    paddingTop: 16,
    borderTop: "1px solid #f3f4f6",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
  },
  legendText: {
    fontSize: 12,
    color: "#6b7280",
  },
  detailCard: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    padding: 20,
    marginBottom: 16,
  },
  detailHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 16,
  },
  detailTitle: {
    fontSize: 16,
    fontWeight: 400,
    color: "#111827",
    margin: 0,
  },
  detailBody: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  detailLabel: {
    fontSize: 13,
    color: "#6b7280",
  },
  detailValue: {
    fontSize: 14,
    fontWeight: 400,
    color: "#374151",
  },
  typeBadge: {
    fontSize: 12,
    fontWeight: 400,
    padding: "4px 12px",
    borderRadius: 12,
  },
};

export default TurnosEmployee;
